/**
 * Where the household is, in time - as a NAME, not a number.
 *
 * WHY  (5 October 2026)
 *
 *   Every analytics call used to send only `tz_offset`: the minutes on the
 *   phone's clock at the moment of the request. Sydney moved from +10:00 to
 *   +11:00 on 4 October, so on the 5th a Sydney phone sent +11:00 and the
 *   server applied it to 30 September as well. A shop at 23:30 on the last
 *   night of September was counted in October's budget.
 *
 *       AN OFFSET IS A SNAPSHOT. A ZONE IS A RULE. ONLY ONE OF THEM KNOWS
 *       WHAT LAST WEEK LOOKED LIKE.
 *
 *   So every call now sends the zone name too ("Australia/Sydney"), and the
 *   server works out the offset that was in force on each date.
 *
 * WHY THE OFFSET IS STILL SENT
 *
 *   A server one release behind ignores `tz` and needs the number, and a
 *   browser too old for Intl's timeZone gives no name at all. Sending both
 *   means neither side has to be upgraded first.
 *
 * WHY ONE FILE
 *
 *   Fifteen call sites built the same query string by hand, and one of them -
 *   price watch - had never sent a zone at all, so its "same day?" check ran
 *   in UTC for as long as it existed. A thing every call needs and one call
 *   forgot is a thing that belongs in one place.
 */

/** The IANA zone name, or "" if the browser cannot say. Never throws. */
export function localZoneName() {
  try {
    const name = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof name === "string" && name ? name : "";
  } catch {
    return "";
  }
}

/**
 * The query-string fragment every analytics call sends, WITHOUT a leading
 * "&" or "?" - the caller joins it. getTimezoneOffset convention (UTC minus
 * local, Perth -480), which is what every endpoint except the purchase
 * calendar expects.
 */
export function tzQuery(at = new Date()) {
  const name = localZoneName();
  const offset = `tz_offset=${at.getTimezoneOffset()}`;
  return name ? `${offset}&tz=${encodeURIComponent(name)}` : offset;
}
